// Tema escuro por padrão, claro por escolha.
//
// Três peças, e a separação entre elas é o que faz a regra valer:
//
//   temaInicial()  — o que vale ao abrir: escolha salva, senão o sistema
//   aplicarTema()  — mexe no DOM, e só
//   lembrarTema()  — persiste, e só é chamada quando alguém ESCOLHE
//
// `aplicarTema` gravava junto, e isso quebrava a própria regra que o arquivo
// declara. Quem abre a interface pela primeira vez com o sistema no claro tinha
// `claro` gravado na montagem — como se tivesse escolhido — e a partir daí a
// preferência do sistema parava de ser seguida para sempre. "A escolha salva
// vence a preferência do sistema" só é verdade se o padrão inferido não for
// gravado como escolha.
//
// O primeiro paint NÃO passa por aqui: quem aplica antes de qualquer pixel é
// `public/tema-inicial.js`, chamado do `<head>`. Este módulo é bundle, e bundle
// é diferido. As duas cópias compartilham a chave abaixo, e há teste comparando.

export type Tema = "escuro" | "claro";

/** Namespaced, e igual à do `public/tema-inicial.js`. */
export const CHAVE_TEMA = "atlas.tema";

/** A escolha salva, se houver. `null` = ninguém escolheu ainda. */
export function temaSalvo(): Tema | null {
  try {
    const salvo = localStorage.getItem(CHAVE_TEMA);
    if (salvo === "escuro" || salvo === "claro") return salvo;
  } catch {
    // Navegador com armazenamento bloqueado. O padrão resolve.
  }
  return null;
}

export function temaInicial(): Tema {
  return temaSalvo() ?? (prefereClaro() ? "claro" : "escuro");
}

function prefereClaro(): boolean {
  try {
    return matchMedia("(prefers-color-scheme: light)").matches;
  } catch {
    return false;
  }
}

export function aplicarTema(t: Tema): void {
  // Só o claro carrega atributo: o escuro é o `:root` puro, e assim a folha de
  // estilo não precisa de um bloco para cada um.
  if (t === "claro") document.documentElement.setAttribute("data-tema", "claro");
  else document.documentElement.removeAttribute("data-tema");
}

/**
 * Grava a escolha.
 *
 * `setItem` numa chave própria, nunca `clear()`: o `localStorage` é da origem
 * inteira, e a sessão do operador não mora nele por decisão de segurança — mas
 * limpar o que não é nosso é a classe de bug que aparece meses depois, em outra
 * tela.
 */
export function lembrarTema(t: Tema): void {
  try {
    localStorage.setItem(CHAVE_TEMA, t);
  } catch {
    // Não poder lembrar a escolha não é motivo para não aplicá-la.
  }
}
